import { ChatBubbleIcon, EnvelopeClosedIcon } from "@radix-ui/react-icons";
import {
  Auth0AccountRole,
  MessageTemplateDetail,
  MessageTemplatesService,
} from "client/openapi";
import { errorText } from "components/Forms/formsApi";
import {
  EmailFrame,
  PreviewNotes,
  TextBubble,
  useTemplatePreview,
} from "components/MessageTemplates/TemplatePreviewParts";
import { ReactNode, useEffect, useState } from "react";
import { messagePreviewHead } from "./AutomationsTabModel";
import { useReference } from "./hooks";
import { toAdmins } from "./model";
import {
  parsePersonKey,
  ROLE_OF_ACCOUNT_ROLE,
  templateById,
} from "./reference";
import type { AutomationDraft, MessageStep, PersonKey } from "./types";

/** How tall a message's email shows; a longer one scrolls inside it. */
const EMAIL_HEIGHT = 260;

const EMAIL_ICON = <EnvelopeClosedIcon aria-hidden />;
const SMS_ICON = <ChatBubbleIcon aria-hidden />;

/** A person as the template preview takes them. */
function recipientOf(key: PersonKey) {
  const { role, id } = parsePersonKey(key);
  const person_type = (
    Object.keys(ROLE_OF_ACCOUNT_ROLE) as Auth0AccountRole[]
  ).find((accountRole) => ROLE_OF_ACCOUNT_ROLE[accountRole] === role);
  return person_type ? { person_type, person_id: Number(id) } : null;
}

/** One way the message goes out: its name, then what it would send. */
function Channel({
  icon,
  name,
  children,
}: {
  icon: ReactNode;
  name: string;
  children: ReactNode;
}) {
  return (
    <section className="mt-3 border-t border-gray-200 pt-3">
      <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-900">
        {icon}
        {name}
      </h4>
      {children}
    </section>
  );
}

/**
 * The step dialog's message, as it would be sent: who it goes to, then the
 * email (when Email is ticked) and the text (when SMS is ticked), filled in
 * by the backend the way it sends them. With nobody to fill it in for, the
 * template as written, with its {{variables}}. Nothing is sent.
 *
 * automation: the draft, as the dialog has it now. saved: the draft as it
 * opened, whose saved message steps can say which meeting or form to fill in
 * (a step that isn't saved as a message isn't a trigger to the backend).
 */
export default function StepMessagePreview({
  automation,
  saved,
  step,
}: {
  automation: AutomationDraft;
  saved: AutomationDraft | null;
  step: MessageStep;
}) {
  const ref = useReference();
  const { templateId, email, sms } = step.message;
  const template = templateById(ref, templateId);
  const head = messagePreviewHead(ref, automation, step);
  const person = template && head.person ? recipientOf(head.person) : null;
  const trigger = saved?.steps.find(
    (s) =>
      s.action === "message" &&
      /^\d+$/.test(s.id) &&
      !!templateById(ref, s.message.templateId)
  );
  const { preview, error } = useTemplatePreview(
    ref.orgId,
    person
      ? {
          template_id: templateId,
          person,
          ...(trigger ? { trigger_id: Number(trigger.id) } : {}),
          ...(toAdmins(step.message.sendTo) ? { for_admins: true } : {}),
        }
      : null
  );
  const [written, setWritten] = useState<MessageTemplateDetail>();
  const [writtenError, setWrittenError] = useState<string>();
  const asWritten = !!template && !person;

  useEffect(() => {
    if (!asWritten || templateId === null) return;
    let live = true;
    MessageTemplatesService.getMessageTemplate({ templateId })
      .then((found) => live && setWritten(found))
      .catch(
        (e) =>
          live &&
          setWrittenError(errorText(e, "The template could not be loaded."))
      );
    return () => {
      live = false;
    };
  }, [asWritten, templateId]);

  if (!template) return null;

  const loading = (
    <div className="mt-2 h-24 animate-pulse rounded bg-gray-200" />
  );
  const noEmail = email && !template.has_email && (
    <p className="mt-2 text-sm text-gray-500">
      This template has no email yet.
    </p>
  );
  const showEmail = email && template.has_email;
  const subject = (words: string | null | undefined) => (
    <p className="text-sm text-gray-700">
      <span className="font-semibold">Subject:</span> {words}
    </p>
  );

  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <p className="break-words text-sm font-semibold text-gray-900">
        {head.to}
      </p>
      {head.as && <p className="text-xs text-gray-500">{head.as}</p>}
      {head.asks && <p className="text-xs text-gray-500">{head.asks}</p>}
      {(error || writtenError) && (
        <p className="mt-2 text-sm text-red-600">{error || writtenError}</p>
      )}
      {!email && !sms ? null : asWritten ? (
        !written ? (
          !writtenError && loading
        ) : (
          <>
            {noEmail}
            {showEmail && (
              <Channel icon={EMAIL_ICON} name="Email">
                {subject(written.email_subject)}
                <div className="mt-1">
                  {written.email_format ===
                  MessageTemplateDetail.email_format.HTML ? (
                    <EmailFrame
                      html={written.email_html ?? ""}
                      title="Email as written"
                      height={EMAIL_HEIGHT}
                    />
                  ) : (
                    <p className="whitespace-pre-wrap break-words rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-900">
                      {written.email_body}
                    </p>
                  )}
                </div>
              </Channel>
            )}
            {sms && (
              <Channel icon={SMS_ICON} name="SMS">
                <TextBubble text={written.sms_body ?? ""} />
              </Channel>
            )}
          </>
        )
      ) : !preview ? (
        !error && loading
      ) : (
        <>
          {noEmail}
          {showEmail && (
            <Channel icon={EMAIL_ICON} name="Email">
              {subject(preview.subject)}
              <div className="mt-1">
                <EmailFrame
                  html={preview.html}
                  title="Email preview"
                  height={EMAIL_HEIGHT}
                />
              </div>
            </Channel>
          )}
          {sms && (
            <Channel icon={SMS_ICON} name="SMS">
              <TextBubble text={preview.sms} />
            </Channel>
          )}
          <PreviewNotes preview={preview} />
        </>
      )}
    </div>
  );
}
